/**
 * WebSocket hook for AI chat communication
 * Manages connection, message streaming, and reconnection
 */

import { useCallback, useEffect, useRef, useState } from "react";

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  isStreaming?: boolean;
}

interface UseChatWebSocketOptions {
  apiKey: string;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

interface UseChatWebSocketReturn {
  messages: ChatMessage[];
  sendMessage: (text: string) => void;
  clearHistory: () => void;
  isConnected: boolean;
  isStreaming: boolean;
  error: Error | null;
}

const WS_URL = import.meta.env.VITE_AI_AGENT_WS_URL || "wss://ai-invoices-agent-api.mono-dev.eoln.eu/chat";

/**
 * Clean up SSE artifacts from message content.
 * The LLM sometimes outputs raw SSE format in its response.
 */
function cleanMessageContent(content: string): string {
  return content
    .split("\n")
    .filter((line) => {
      const trimmed = line.trim();
      // Filter out SSE-formatted lines
      if (trimmed.startsWith("data:") && trimmed.includes("{")) {
        return false;
      }
      // Filter out [DONE] markers
      if (trimmed === "data: [DONE]" || trimmed === "[DONE]") {
        return false;
      }
      return true;
    })
    .join("\n")
    .trim();
}

export function useChatWebSocket({ apiKey, onError, onComplete }: UseChatWebSocketOptions): UseChatWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Use refs for callbacks to avoid reconnection when callbacks change
  const onErrorRef = useRef(onError);
  const onCompleteRef = useRef(onComplete);
  onErrorRef.current = onError;
  onCompleteRef.current = onComplete;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const ws = new WebSocket(`${WS_URL}?apiKey=${encodeURIComponent(apiKey)}`);

    ws.onopen = () => {
      setIsConnected(true);
      setError(null);
    };

    ws.onmessage = (event) => {
      const rawData = event.data as string;

      // Process each line separately (SSE sends multiple lines)
      const lines = rawData.split("\n").filter((line) => line.trim());

      for (const line of lines) {
        const trimmedLine = line.trim();

        // Strip "data: " prefix if present (SSE format)
        let payload = trimmedLine;
        if (payload.startsWith("data:")) {
          payload = payload.slice(5).trim();
        }

        // Skip empty payloads
        if (!payload) {
          continue;
        }

        // Check for stream completion
        if (payload === "[DONE]") {
          setIsStreaming(false);
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.isStreaming) {
              const cleanedContent = cleanMessageContent(last.content);
              return [...prev.slice(0, -1), { ...last, content: cleanedContent, isStreaming: false }];
            }
            return prev;
          });
          onCompleteRef.current?.();
          continue;
        }

        try {
          const data = JSON.parse(payload) as {
            type?: string;
            data?: string;
            message?: string;
            response?: string;
            usage?: unknown;
          };

          // Handle AI Agent response format: {"response": "...", "p": "..."}
          if ("response" in data && typeof data.response === "string") {
            const responseText = data.response;
            if (responseText.length > 0) {
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.isStreaming && last.role === "assistant") {
                  return [...prev.slice(0, -1), { ...last, content: last.content + responseText }];
                }
                return [
                  ...prev,
                  {
                    id: crypto.randomUUID(),
                    role: "assistant",
                    content: responseText,
                    timestamp: new Date().toISOString(),
                    isStreaming: true,
                  },
                ];
              });
            }
            continue;
          }

          // Handle legacy format with type field
          switch (data.type) {
            case "connected":
              // Connection established
              break;

            case "message_received":
              // Server acknowledged message
              break;

            case "chunk": {
              // data.data contains SSE format: "data: {\"response\":\"...\"}\\n\\n"
              let chunkText = "";
              if (data.data) {
                let inner = data.data.trim();
                if (inner.startsWith("data:")) {
                  inner = inner.slice(5).trim();
                }
                // Handle [DONE] marker inside chunk
                if (inner === "[DONE]") {
                  setIsStreaming(false);
                  setMessages((prev) => {
                    const last = prev[prev.length - 1];
                    if (last?.isStreaming) {
                      const cleanedContent = cleanMessageContent(last.content);
                      return [...prev.slice(0, -1), { ...last, content: cleanedContent, isStreaming: false }];
                    }
                    return prev;
                  });
                  onCompleteRef.current?.();
                  break;
                }
                try {
                  const parsed = JSON.parse(inner) as { response?: string };
                  if (parsed.response) {
                    chunkText = parsed.response;
                  }
                } catch {
                  // Ignore parse errors - don't fallback to raw data
                }
              }
              if (chunkText) {
                setMessages((prev) => {
                  const last = prev[prev.length - 1];
                  if (last?.isStreaming) {
                    return [...prev.slice(0, -1), { ...last, content: last.content + chunkText }];
                  }
                  return [
                    ...prev,
                    {
                      id: crypto.randomUUID(),
                      role: "assistant",
                      content: chunkText,
                      timestamp: new Date().toISOString(),
                      isStreaming: true,
                    },
                  ];
                });
              }
              break;
            }

            case "done":
              setIsStreaming(false);
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.isStreaming) {
                  const cleanedContent = cleanMessageContent(last.content);
                  return [...prev.slice(0, -1), { ...last, content: cleanedContent, isStreaming: false }];
                }
                return prev;
              });
              onCompleteRef.current?.();
              break;

            case "error": {
              setIsStreaming(false);
              const err = new Error(data.message || "Unknown error");
              setError(err);
              onErrorRef.current?.(err);
              break;
            }

            case "history_cleared":
              setMessages([]);
              break;
          }
        } catch {
          // Ignore parse errors for this line
        }
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      const err = new Error("WebSocket connection error");
      setError(err);
      onErrorRef.current?.(err);
    };

    wsRef.current = ws;
  }, [apiKey]);

  useEffect(() => {
    connect();

    return () => {
      clearTimeout(reconnectTimeoutRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendMessage = useCallback((text: string) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      return;
    }

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsStreaming(true);

    wsRef.current.send(JSON.stringify({ type: "message", text }));
  }, []);

  const clearHistory = useCallback(() => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      return;
    }
    wsRef.current.send(JSON.stringify({ type: "clear_history" }));
  }, []);

  return {
    messages,
    sendMessage,
    clearHistory,
    isConnected,
    isStreaming,
    error,
  };
}
